import React, { Component } from 'react'
import { connect } from 'react-redux'
import { Modal } from 'antd'
import LangContext from 'modules/shared/context/langContext'
import * as authActions from 'modules/auth/actions'
import {
  refreshSession,
  endSession,
  isAccountLockedRefreshError,
  getRefreshToken,
  isNearExpiry,
  nowSeconds,
  REFRESH_AHEAD_SECONDS,
} from 'lib/webSession'

export const SESSION_EXPIRED_EVENT = 'web-session-expired'
export const MIN_RETRY_MS = 60000

/**
 * Keeps a web session alive on every logged-in screen (Layout mounts it
 * whenever `done` is true — also screens without Header such as the command
 * center dashboard). Refreshes REFRESH_AHEAD_SECONDS before the access token
 * expires and whenever the tab becomes visible again.
 *
 * Does nothing without a refresh token: those sessions keep Header's popup at
 * expiry (old behavior).
 *
 * When a refresh fails it fires SESSION_EXPIRED_EVENT (cancelable). Header
 * handles it with its full logout popup; with no Header mounted the keeper
 * ends the session itself.
 */
export class WebSessionKeeper extends Component {
  componentDidMount() {
    this._isMounted = true
    this.schedule()
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  }

  componentDidUpdate(prevProps) {
    if (prevProps.tokenExp !== this.props.tokenExp) this.schedule()
  }

  componentWillUnmount() {
    this._isMounted = false
    clearTimeout(this.timer)
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
  }

  schedule = () => {
    clearTimeout(this.timer)
    const { tokenExp } = this.props
    if (!getRefreshToken() || typeof tokenExp !== 'number') return
    let wait = Math.max(0, tokenExp - REFRESH_AHEAD_SECONDS - nowSeconds()) * 1000
    // never hammer the BE if a refresh just returned a short-lived token
    if (wait === 0 && this.lastRefreshAt && Date.now() - this.lastRefreshAt < MIN_RETRY_MS) wait = MIN_RETRY_MS
    this.timer = setTimeout(this.refresh, wait)
  }

  refresh = () => {
    this.lastRefreshAt = Date.now()
    return refreshSession()
      .then(() => {
        if (this._isMounted) this.schedule()
      })
      .catch((e) => {
        // locked: refreshSession already sent the tab to the account-locked view
        // (DECISIONS #27) — no "session expired" popup on top of it
        if (isAccountLockedRefreshError(e)) return
        if (this._isMounted) this.expire()
      })
  }

  // background tabs throttle timers and sleep pauses them — check on return
  handleVisibilityChange = () => {
    if (document.visibilityState !== 'visible' || !getRefreshToken()) return
    if (isNearExpiry(this.props.tokenExp)) this.refresh()
  }

  expire = () => {
    const event = new CustomEvent(SESSION_EXPIRED_EVENT, { cancelable: true })
    const unhandled = window.dispatchEvent(event)
    if (!unhandled) return // Header showed its popup and logged out

    endSession()
    this.props.removeCredentials()
    Modal.warning({
      title: <LangContext.Consumer>{(i18n) => i18n.n.notification}</LangContext.Consumer>,
      content: <LangContext.Consumer>{(i18n) => i18n.t.tokenExpired}</LangContext.Consumer>,
      okText: 'Ok',
      okType: 'default',
    })
  }

  render() {
    return null
  }
}

const mapStateToProps = ({ auth: { tokenExp } }) => ({ tokenExp })

export default connect(mapStateToProps, { removeCredentials: authActions.removeCredentials })(WebSessionKeeper)
